import { useFactures } from '../../api/ventes.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { useLangue } from '../../composants/useLangue.js';
import { texteEnLangue } from '@artosera/shared';
import { Select } from '@artosera/ui';
import type { CibleRattachement } from '@artosera/shared';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useArtistes } from '../../api/artistes.js';
import { useContacts } from '../../api/contacts.js';
import { useExpositions } from '../../api/expositions.js';
import { useOeuvres } from '../../api/oeuvres.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { RattachementPastille } from './RattachementPastille.js';
import type { RattachementLu } from './useRattachementResolu.js';
import styles from './RechercheRattachement.module.css';

/** Les cibles de base ouvertes du coffre (§ 18, `CIBLES_OUVERTES` côté serveur). */
const CIBLES_DE_BASE: readonly CibleRattachement[] = ['artwork', 'artist', 'contact', 'exhibition'];

export interface RechercheRattachementProps {
  galerieSlug: string;
  valeurs: readonly RattachementLu[];
  onChanger: (valeurs: readonly RattachementLu[]) => void;
}

/**
 * Rattachements par recherche (3.3, écran 4 : dialogue de dépose et fiche) :
 * une cible à la fois (œuvre, artiste, contact, exposition, facture), résultats en
 * direct, puces retirables en dessous. Le format envoyé à l'API est
 * `type:id` (§ 18.4) ; `CoffreApi.deposer`/`modifier` fait la conversion.
 */
function RechercheRattachementDansLaPortee({
  galerieSlug,
  valeurs,
  onChanger,
}: RechercheRattachementProps): JSX.Element {
  const langue = useLangue();
  const { t } = useTranslation(['regie', 'commun']);
  const { galerie } = useShellContext();
  const avecFactures =
    galerieAModule(galerie, 'facturation') && galerieALaction(galerie, 'invoices:read');
  const [cible, setCible] = useState<CibleRattachement>('artwork');
  const [recherche, setRecherche] = useState('');
  // Le délai appartient à une cible et à son texte, jamais à la saisie seule.
  const saisie = useMemo(() => ({ cible, texte: recherche }), [cible, recherche]);
  const saisieDebattue = useDebounce(saisie, 300);
  const rechercheDebattue = saisieDebattue.texte;
  const actif =
    recherche.trim().length > 0 &&
    saisieDebattue.cible === cible &&
    rechercheDebattue === recherche;

  const factures = useFactures(
    galerieSlug,
    { q: rechercheDebattue || undefined, parPage: 8 },
    { enabled: actif && cible === 'invoice' && avecFactures },
  );
  const cibles = avecFactures ? [...CIBLES_DE_BASE, 'invoice' as const] : CIBLES_DE_BASE;

  const oeuvres = useOeuvres(
    galerieSlug,
    { q: rechercheDebattue || undefined, parPage: 8 },
    { enabled: actif && cible === 'artwork' },
  );
  const artistes = useArtistes(
    galerieSlug,
    { q: rechercheDebattue || undefined, tri: 'nom', parPage: 8 },
    { enabled: actif && cible === 'artist' },
  );
  const contacts = useContacts(
    galerieSlug,
    { q: rechercheDebattue || undefined, tri: 'nom', parPage: 8 },
    { enabled: actif && cible === 'contact' },
  );
  const expositions = useExpositions(
    galerieSlug,
    { q: rechercheDebattue || undefined, parPage: 8 },
    { enabled: actif && cible === 'exhibition' },
  );

  const requeteCourante =
    cible === 'invoice'
      ? factures
      : cible === 'artwork'
        ? oeuvres
        : cible === 'artist'
          ? artistes
          : cible === 'contact'
            ? contacts
            : expositions;

  function resultats(): { id: string; label: string }[] {
    // Le placeholder de pagination ne constitue pas le résultat de cette saisie.
    if (!actif || requeteCourante.isPlaceholderData) return [];
    switch (cible) {
      case 'artwork':
        return (oeuvres.data?.oeuvres ?? []).map((o) => ({
          id: o.id,
          label: `${o.artisteNom} — ${o.titre}`,
        }));
      case 'artist':
        return (artistes.data?.artistes ?? []).map((a) => ({ id: a.id, label: a.nom }));
      case 'contact':
        return (contacts.data?.contacts ?? []).map((c) => ({ id: c.id, label: c.nomAffiche }));
      case 'exhibition':
        return (expositions.data?.expositions ?? []).map((e) => ({
          id: e.id,
          label: texteEnLangue(e.titre, langue),
        }));
      case 'invoice':
        return avecFactures
          ? (factures.data?.factures ?? []).map((f) => ({
              id: f.id,
              label: `${f.numero ?? t('regie:coffre.rattachements.factureBrouillon')}${f.contactNom ? ` — ${f.contactNom}` : ''}`,
            }))
          : [];
      default:
        return [];
    }
  }

  function chargement(): boolean {
    if (requeteCourante.isPlaceholderData && requeteCourante.isFetching) return true;
    if (cible === 'invoice') return factures.isLoading;
    if (cible === 'artwork') return oeuvres.isLoading;
    if (cible === 'artist') return artistes.isLoading;
    if (cible === 'contact') return contacts.isLoading;
    return expositions.isLoading;
  }

  function ajouter(id: string): void {
    if (valeurs.some((valeur) => valeur.type === cible && valeur.id === id)) {
      setRecherche('');
      return;
    }
    onChanger([...valeurs, { type: cible, id }]);
    setRecherche('');
  }

  function retirer(type: CibleRattachement, id: string): void {
    onChanger(valeurs.filter((valeur) => !(valeur.type === type && valeur.id === id)));
  }

  return (
    <div className={styles.enveloppe}>
      <div className={styles.ligne}>
        <Select
          aria-label={t('regie:coffre.rattachements.cibleChamp')}
          value={cible}
          onValueChange={(valeur) => {
            setCible(valeur as CibleRattachement);
            setRecherche('');
          }}
          options={cibles.map((code) => ({
            value: code,
            label: t(`regie:coffre.rattachements.cibles.${code}`),
          }))}
        />
        <div className={styles.recherche}>
          <input
            type="search"
            className={styles.saisie}
            placeholder={t('regie:coffre.rattachements.rechercheChamp')}
            aria-label={t('regie:coffre.rattachements.rechercheChamp')}
            value={recherche}
            onChange={(event) => setRecherche(event.target.value)}
          />
          {actif ? (
            <div className={styles.resultats}>
              {chargement() ? (
                <p className={styles.vide}>{t('commun:chargement')}</p>
              ) : resultats().length === 0 ? (
                <p className={styles.vide}>{t('regie:coffre.rattachements.aucunResultat')}</p>
              ) : (
                resultats().map((resultat) => (
                  <button
                    key={resultat.id}
                    type="button"
                    className={styles.resultatLigne}
                    onClick={() => ajouter(resultat.id)}
                  >
                    {resultat.label}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>

      {valeurs.length > 0 ? (
        <div className={styles.puces}>
          {valeurs.map((valeur) => (
            <RattachementPastille
              key={`${valeur.type}:${valeur.id}`}
              galerieSlug={galerieSlug}
              rattachement={valeur}
              onRetirer={() => retirer(valeur.type, valeur.id)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Une nouvelle portée démonte aussi le debounce et les résultats déjà lus. */
export function RechercheRattachement(props: RechercheRattachementProps): JSX.Element {
  const { galerie, compte } = useShellContext();
  const avecFactures =
    galerieAModule(galerie, 'facturation') && galerieALaction(galerie, 'invoices:read');
  return (
    <RechercheRattachementDansLaPortee
      key={`${compte.id}:${props.galerieSlug}:${avecFactures}`}
      {...props}
    />
  );
}
